import { Page } from "../types/Authorization";
import { ReportOption, visibleReports } from "../pages/reports/reportCatalog";

/**
 * "Show me …" → the matching report (roadmap #25). By code: the typed words
 * against each report's name, module and description, plus everyday words
 * people use for it. Only reports the person may open are considered, so a
 * match never points at a page they can't see. No AI, no message used.
 */

/** Everyday words for a report, keyed "tab:report". */
const EVERYDAY: Record<string, RegExp> = {
    "finance:ar-aging": /\b(owe us|owes us|overdue|unpaid|receivables?|debtors?|collect(ion)?s?|outstanding invoices?)\b|متأخر/i,
    "finance:ap-aging": /\b(we owe|payables?|creditors?|unpaid bills?|bills due|suppliers? to pay)\b/i,
    "finance:pl": /\b(p\s*&\s*l|p and l|profit|loss|income statement|revenue vs cost)\b|أرباح/i,
    "finance:cash-flow": /\b(cash|payments? (in|out)|money (in|out)|received|paid out)\b/i,
    "finance:tax": /\b(tax|vat)\b|ضريبة/i,
    "missions:financial": /\b(margins?|mission profit|profit per mission|profitab)/i,
    "missions:incidents-expenses": /\b(incidents?|expenses?|safety)\b/i,
    "missions:resources": /\b(utili[sz]ation|workload|assets? used|team usage)\b/i,
    "missions:portfolio": /\b(missions? (overview|status|count)|how many missions)\b/i,
    "mission-requests:pipeline": /\b(pipeline|requests? (status|funnel)|quotes? sent)\b/i,
    "mission-requests:aging": /\b(stale|stuck|old) requests?\b/i,
    "mission-requests:service-demand": /\b(demand|most requested|popular services?)\b/i,
    "leads:funnel": /\b(lead funnel|conversion)\b/i,
    "leads:drop": /\b(lost|dropped) leads?|why (we )?lose\b/i,
    "clients:revenue": /\b(top clients?|best clients?|revenue by client|biggest clients?)\b/i,
    "people:headcount": /\b(headcount|joiners?|leavers?|turnover|how many (staff|employees|people))\b/i,
    "people:leave": /\b(leave|days off|vacations?|holidays taken)\b|إجازات/i,
    "people:attendance": /\b(attendance|late(ness)?|no.?shows?|check.?ins?)\b/i,
    "people:payroll": /\b(payroll|salar(y|ies)|wages?)\b|رواتب/i,
    "people:spend": /\b(claims?|petty cash|purchases?|office spend|budgets?)\b/i,
    "people:documents": /\b(expir(ed|ing)|missing documents?|compliance)\b/i,
    "management:executive": /\b(at a glance|overview of the business|how are we doing|business summary)\b/i,
    "management:performance": /\b(scorecard|top performers?|kpis?)\b/i,
    "management:efficiency": /\b(bottlenecks?|efficiency|how long .* take)\b/i,
};

/** Words that say "open a report". */
export const REPORT_WORDS = /\b(show me|report|reports|statement|breakdown|analysis|chart)\b|تقرير|اعرض/i;

const STOP = new Set(["show", "me", "the", "a", "an", "of", "for", "our", "my", "all", "report", "reports", "and", "in", "on", "by", "with", "what", "which", "who", "is", "are"]);
const wordsOf = (text: string) => text.toLowerCase().split(/[^\p{L}\p{N}&]+/u).filter((w) => w.length >= 3 && !STOP.has(w));

export interface ReportMatch {
    tab: string;
    tabLabel: string;
    report: ReportOption;
    to: string;
    score: number;
}

/**
 * The reports that match typed words, best first (at most `max`).
 * @param text - What was typed.
 * @param pages - The person's pages (only reports they may open are matched).
 */
export const findReports = (text: string, pages: Page[], max = 2): ReportMatch[] => {
    const words = wordsOf(text);
    if (!words.length && !Object.values(EVERYDAY).some((re) => re.test(text))) return [];
    return visibleReports(pages)
        .map(({ tab, tabLabel, report }) => {
            const label = report.label.toLowerCase();
            const description = report.description.toLowerCase();
            const module = tabLabel.toLowerCase();
            let score = EVERYDAY[`${tab}:${report.key}`]?.test(text) ? 4 : 0;
            words.forEach((word) => {
                if (label.includes(word)) score += 3;
                else if (module.includes(word)) score += 1;
                else if (description.includes(word)) score += 1;
            });
            return { tab, tabLabel, report, to: `/reports?tab=${tab}&report=${report.key}`, score };
        })
        .filter((match) => match.score >= 3)
        .sort((a, b) => b.score - a.score)
        .slice(0, max);
};
